import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { Button, HStack, IllusLocation, useResponsive } from '@garnish/core';
import { Modal, ModalDialogue } from '@garnish/overlays';

import { useTelemetry } from '@order/Telemetry';

import { useCart } from '../../hooks';

// Warning for when the customer attempts to edit an address that has a order associated with it.
// Since the address is linked to the order object in gravy, changing the address requires clearing the bag.
export const ActiveBagWarning = ({ visible, onClose, onConfirm }: ActiveBagWarningProps) => {
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { currentBreakpoint } = useResponsive();

  useEffect(() => {
    if (!visible) return;
    track('addresses.active_bag');
  }, [track, visible]);

  return (
    <ModalDialogue
      testID="addresses.active-bag-warning.container"
      size="medium"
      imageSrc={currentBreakpoint.isXS ? undefined : IllusLocation}
      visible={visible}
      accessibilityLabel={formatMessage(messages.addressesActiveBagWarningTitle)}
      headline={formatMessage(messages.addressesActiveBagWarningTitle)}
      body={formatMessage(messages.addressesActiveBagWarningSubtitle)}
      onRequestClose={onClose}
    >
      <WarningControls onClose={onClose} onConfirm={onConfirm} />
    </ModalDialogue>
  );
};

// Intercepts an attempt to edit an address.
// Will only allow it if it's not the address for the current bag.
// Returns callbacks to be used in the `<ActiveBagWarning />` modal.
export const useActiveBagWarning = (proceedToEdit: (addressId: string) => void) => {
  // ─────────── State ───────────
  const { cart, isCartEmpty } = useCart();
  const currentBagAddressId = !isCartEmpty && cart?.deliveryOrderDetail?.address?.id;
  const [activeBagWarning, setActiveBagWarning] = React.useState('');

  // ────── Modal Callbacks ──────
  const closeActiveBagWarning = React.useCallback(() => {
    setActiveBagWarning('');
  }, [setActiveBagWarning]);
  const confirmEditAddress = React.useCallback(() => {
    proceedToEdit(activeBagWarning);
    closeActiveBagWarning();
  }, [activeBagWarning, proceedToEdit, closeActiveBagWarning]);

  // ───── Actual Edit Request ─────
  const onEdit = React.useCallback(
    (addressId: string) => {
      if (addressId === currentBagAddressId) {
        setActiveBagWarning(addressId);

        return;
      }

      proceedToEdit(addressId);
    },
    [currentBagAddressId, proceedToEdit],
  );

  // ────── Component Usage ──────
  return {
    activeBagWarning,
    closeActiveBagWarning,
    confirmEditAddress,
    onEdit,
  };
};

const WarningControls = ({
  onClose,
  onConfirm,
}: Pick<ActiveBagWarningProps, 'onClose' | 'onConfirm'>) => {
  const { formatMessage } = useIntl();

  return (
    <Modal.Footer withoutTopBorder>
      <SafeAreaView edges={['bottom']}>
        <HStack itemsPerRow={2} gap={theme.spacing['4']}>
          <Button
            palette="secondary"
            size="large"
            testID="addresses.active-bag-warning.close"
            accessibilityLabel={formatMessage(messages.activeBagWarningClose)}
            accessibilityHint={formatMessage(messages.activeBagWarningClose)}
            accessibilityRole="button"
            onPress={onClose}
          >
            {formatMessage(messages.activeBagWarningClose)}
          </Button>
          <Button
            size="large"
            testID="addresses.active-bag-warning.confirm"
            accessibilityLabel={formatMessage(messages.addressesActiveBagWarningCta)}
            accessibilityHint={formatMessage(messages.addressesActiveBagWarningCta)}
            accessibilityRole="button"
            onPress={onConfirm}
          >
            {formatMessage(messages.addressesActiveBagWarningCta)}
          </Button>
        </HStack>
      </SafeAreaView>
    </Modal.Footer>
  );
};

type ActiveBagWarningProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
}>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addressesActiveBagWarningTitle: {
    defaultMessage: 'You have an active bag with this address.',
    description: 'Addresses | Active Bag Warning | Title',
  },
  addressesActiveBagWarningSubtitle: {
    defaultMessage: 'If you proceed to edit or remove this address your bag will be cleared.',
    description: 'Addresses | Active Bag Warning | Subtitle',
  },
  addressesActiveBagWarningCta: {
    defaultMessage: 'Edit address',
    description: 'Addresses | Active Bag Warning | Cta',
  },
  activeBagWarningClose: {
    defaultMessage: 'Close',
    description: 'Active Bag Warning | Close',
  },
});
